import React, { useEffect, useState } from 'react';
import Swal from '../Librarian/librarianSwal';
import { useAuth } from '../Constant/Auth';
import { useActiveLibraryId } from '../Constant/useActiveLibrary';
import {
  getRolesList,
  getAllLibrariesAdmin,
  sendPasswordResetOtp,
  verifyEmailOtp,
  resetPassword,
  getErrorMessage
} from '../api/api';
import '../Librarian/lib-page-header.css';
import '../Librarian/BookList.css';

/**
 * My Profile — the logged-in user's own account. Details are read-only (there is
 * no "update my profile" endpoint), but the password can be changed securely via
 * the existing email-OTP flow: send OTP -> verify OTP -> set new password.
 */
const MyProfile = () => {
  const { user } = useAuth();
  const libraryId = useActiveLibraryId();

  const [roleName, setRoleName] = useState('');
  const [schoolName, setSchoolName] = useState('');

  // password change
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [roles, libs] = await Promise.all([
          getRolesList().catch(() => []),
          getAllLibrariesAdmin().catch(() => [])
        ]);
        const r = (roles || []).find((x) => String(x.id) === String(user?.role_id));
        setRoleName(r ? r.name || r.role_name : '');
        const l = (libs || []).find((x) => String(x.id) === String(libraryId));
        setSchoolName(l ? l.library_name : '');
      } catch {
        /* non-critical */
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, libraryId]);

  const fullName = `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || '—';

  const handleSendOtp = async () => {
    if (!user?.email) {
      Swal.fire('No email', 'Your account has no email on file.', 'warning');
      return;
    }
    try {
      setBusy(true);
      await sendPasswordResetOtp(user.email);
      setOtpSent(true);
      Swal.fire('OTP sent', `A verification code was sent to ${user.email}.`, 'success');
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setBusy(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!otp) return Swal.fire('OTP required', 'Enter the code sent to your email.', 'warning');
    if (newPassword.length < 6) return Swal.fire('Weak password', 'Use at least 6 characters.', 'warning');
    if (newPassword !== confirmPassword) return Swal.fire('Mismatch', 'Passwords do not match.', 'warning');

    try {
      setBusy(true);
      const v = await verifyEmailOtp(user.email, otp);
      if (!v?.success) {
        Swal.fire('Invalid OTP', v?.message || 'The code is incorrect or expired.', 'error');
        return;
      }
      await resetPassword(user.email, newPassword);
      Swal.fire('Password changed', 'Your password has been updated.', 'success');
      setOtpSent(false);
      setOtp('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      Swal.fire('Error', getErrorMessage(error), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="container-fluid mt-3">
      <div className="lib-page-header ms-0">
        <div className="lib-page-header__left">
          <div className="lib-page-header__icon"><i className="fas fa-user" /></div>
          <div>
            <h3 className="lib-page-header__title">My Profile</h3>
            <div className="lib-page-header__subtitle">Your account details and password</div>
          </div>
        </div>
      </div>

      <div className="book-table-card" style={{ padding: 24, marginBottom: 18 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 20 }}>
          <Detail label="Name" value={fullName} />
          <Detail label="Email" value={user?.email} />
          <Detail label="Mobile" value={user?.mobile_no} />
          <Detail label="Role" value={roleName || `Role #${user?.role_id ?? ''}`} />
          <Detail label="School" value={schoolName || (libraryId ? `#${libraryId}` : '—')} />
        </div>
      </div>

      <div className="book-table-card" style={{ padding: 24, maxWidth: 560 }}>
        <h4 style={{ margin: '0 0 4px', fontSize: 16, fontWeight: 800, color: '#172033' }}>Change Password</h4>
        <p style={{ margin: '0 0 18px', fontSize: 13, color: '#6b7a99' }}>
          We'll email a verification code to <strong>{user?.email || 'your address'}</strong> before changing it.
        </p>

        {!otpSent ? (
          <button className="book-add-btn" onClick={handleSendOtp} disabled={busy}>
            <i className="fas fa-paper-plane" /> {busy ? 'Sending...' : 'Send verification code'}
          </button>
        ) : (
          <div className="cat-form">
            <div className="cat-form-row">
              <div className="cat-form-group">
                <label>Verification Code</label>
                <input value={otp} onChange={(e) => setOtp(e.target.value)} placeholder="Code from your email" />
              </div>
            </div>
            <div className="cat-form-row">
              <div className="cat-form-group">
                <label>New Password</label>
                <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
              </div>
              <div className="cat-form-group">
                <label>Confirm New Password</label>
                <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
              </div>
            </div>
            <div className="cat-form-actions">
              <button className="cat-btn-save" onClick={handleChangePassword} disabled={busy}>
                {busy ? 'Saving...' : 'Update Password'}
              </button>
              <button className="cat-btn-cancel" onClick={() => setOtpSent(false)} disabled={busy}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Detail = ({ label, value }) => (
  <div>
    <div style={{ fontSize: 12, fontWeight: 700, color: '#8a96ad', textTransform: 'uppercase', letterSpacing: '.04em' }}>
      {label}
    </div>
    <div style={{ fontSize: 17, fontWeight: 700, color: '#1f2d50', marginTop: 4, wordBreak: 'break-word' }}>{value || '—'}</div>
  </div>
);

export default MyProfile;
